/*
 * Deterministic reads of a sequence, computed in the browser from data the page
 * has already loaded. No API call, no model call -- so these are instant, free,
 * and cannot be wrong about the numbers. The model's own read arrives
 * separately from /copilot/insights and sits underneath these.
 *
 * Rules, in order of what a user would want to hear first:
 *   - anything actively harmful (bounce rate)
 *   - the specific step that is failing, rather than a sequence-level shrug
 *   - good news, when there is some
 *   - what the sequence is missing (composition)
 * A finding is only produced when the numbers behind it are strong enough to
 * mean something; otherwise we say nothing rather than guess.
 */

// Below this many sends a percentage is noise, so no rate-based finding fires.
export const MIN_SENDS = 50;
export const BENCHMARKS = { openRate: 40, clickRate: 2, replyRate: 3 };
const BOUNCE_ALERT_PCT = 5;

const pct = (part, whole) =>
  whole > 0 ? Math.round(((part || 0) / whole) * 1000) / 10 : 0;

const EMAIL_TYPES = ["email", "manualEmail"];

// steps is keyed by step number; each variant carries the channel type and the
// analytics merged in by setOverviewData().
export const readSteps = (steps = {}) =>
  Object.keys(steps || {})
    .map(key => {
      const step = steps[key] || {};
      const variants = step.variants || {};
      const variantKeys = Object.keys(variants);
      const type = variantKeys.length ? variants[variantKeys[0]]?.type || "" : "";
      const stats = variantKeys.map(vk => ({
        key: vk,
        sent: Number(variants[vk]?.sentCount) || 0,
        opened: Number(variants[vk]?.openCount) || 0,
        replied: Number(variants[vk]?.replyCount) || 0
      }));
      return {
        no: Number(key),
        type,
        variants: stats,
        sent: stats.reduce((a, v) => a + v.sent, 0),
        opened: stats.reduce((a, v) => a + v.opened, 0),
        replied: stats.reduce((a, v) => a + v.replied, 0)
      };
    })
    .filter(step => Number.isFinite(step.no))
    .sort((a, b) => a.no - b.no);

export const sequenceFindings = (analytics = {}, steps = {}) => {
  const found = [];
  const sent = Number(analytics.emailsSent) || 0;
  const bounced = Number(analytics.bouncedCount ?? analytics.emailsBounced) || 0;
  const replied = Number(analytics.noOfCampaignsGotReply) || 0;
  const meetings = Number(analytics.meetingBookedCount) || 0;
  const contacts = Number(analytics.contactsAdded) || 0;
  const list = readSteps(steps);

  // Nothing has run. Say so plainly and stop -- every rate below would be 0/0.
  if (!sent) {
    found.push({
      tone: "neutral",
      key: "nothing_sent",
      label: "Nothing sent yet",
      text: contacts
        ? `${contacts} contact${contacts === 1 ? "" : "s"} enrolled, but no emails have gone out yet, so there is no performance to judge.`
        : "No contacts and no sends yet, so there is nothing to measure."
    });
  } else {
    if (bounced > 0 && pct(bounced, sent) >= BOUNCE_ALERT_PCT) {
      found.push({
        tone: "bad",
        key: "deliverability",
        label: "Deliverability",
        text: `${bounced} of ${sent} sends bounced — ${pct(bounced, sent)}%, above the ${BOUNCE_ALERT_PCT}% line where inbox providers start throttling you.`
      });
    }

    // The failing step, named. Only compares steps that have run enough to mean
    // something, and only speaks when one is clearly worse than the rest.
    const ranked = list.filter(step => step.sent >= MIN_SENDS);
    if (ranked.length >= 2) {
      const withRate = ranked.map(step => ({ ...step, rate: pct(step.replied, step.sent) }));
      const worst = withRate.reduce((a, b) => (b.rate < a.rate ? b : a));
      const rest = withRate.filter(step => step.no !== worst.no);
      const restRate = pct(
        rest.reduce((a, s) => a + s.replied, 0),
        rest.reduce((a, s) => a + s.sent, 0)
      );
      if (restRate - worst.rate >= 2) {
        found.push({
          tone: "bad",
          key: "weak_step",
          step: worst.no,
          label: `Step ${worst.no} is the weak point`,
          text: `Step ${worst.no} has gone out ${worst.sent} times for ${worst.replied} repl${worst.replied === 1 ? "y" : "ies"} (${worst.rate}%). Your other steps average ${restRate}%.`
        });
      }
    }

    // A/B variants, where one has clearly won.
    list.forEach(step => {
      const live = step.variants.filter(v => v.sent >= MIN_SENDS);
      if (live.length < 2) return;
      const rated = live.map(v => ({ ...v, rate: pct(v.replied, v.sent) }));
      const best = rated.reduce((a, b) => (b.rate > a.rate ? b : a));
      const worst = rated.reduce((a, b) => (b.rate < a.rate ? b : a));
      if (best.rate - worst.rate >= 2) {
        found.push({
          tone: "good",
          key: "winning_variant",
          step: step.no,
          label: `Step ${step.no} has a winning variant`,
          text: `One variant is replying at ${best.rate}% against ${worst.rate}% for the other, over ${best.sent} and ${worst.sent} sends. Worth sending the rest to the winner.`
        });
      }
    });

    if (sent >= MIN_SENDS) {
      const replyRate = pct(replied, sent);
      if (replyRate >= BENCHMARKS.replyRate) {
        found.push({
          tone: "good",
          key: "performing",
          label: "Performing well",
          text: `${replied} replies from ${sent} sends — ${replyRate}%, above the ${BENCHMARKS.replyRate}% benchmark.`
        });
      }
    }

    const opened = list.reduce((a, s) => a + (s.opened || 0), 0);
    if (sent >= MIN_SENDS && opened > 0) {
      const openRate = pct(opened, sent);
      const replyRate = pct(replied, sent);
      if (openRate >= BENCHMARKS.openRate && replyRate < 2) {
        found.push({
          tone: "warn",
          key: "opens_high_replies_low",
          label: "Read, not answered",
          text: `${openRate}% open but only ${replyRate}% reply. The subject works; the body does not. Try a shorter opener with one clear ask.`
        });
      } else if (openRate < 25 && replyRate < BENCHMARKS.replyRate) {
        found.push({
          tone: "warn",
          key: "opens_low",
          label: "Low opens",
          text: `${openRate}% open on ${sent} sends. Check the subject line and your domain health before changing the body.`
        });
      }
    }

    if (meetings > 0) {
      found.push({
        tone: "good",
        key: "meetings",
        label: "Meetings booked",
        text: `This sequence has booked ${meetings} meeting${meetings === 1 ? "" : "s"}.`
      });
    }
  }

  // Composition: what the sequence is missing, regardless of volume.
  if (list.length) {
    const types = new Set(list.map(step => step.type).filter(Boolean));
    const allEmail = [...types].every(type => EMAIL_TYPES.includes(type));
    if (allEmail && types.size > 0) {
      found.push({
        tone: "neutral",
        key: "email_only",
        label: "Email only",
        text: `All ${list.length} step${list.length === 1 ? "" : "s"} are email. A LinkedIn connection request or a call step reaches people who never open email — both can be added as steps here.`
      });
    }
    if (list.length === 1) {
      found.push({
        tone: "neutral",
        key: "no_follow_up",
        label: "No follow-up",
        text: "There is only one step. Most replies to cold outbound come from the second and third touch, not the first."
      });
    }
  }

  return found;
};

export default sequenceFindings;

/*
 * Contact outcomes — what actually happened to the people in this sequence.
 *
 * Ordered by what a seller can still do something about, which is close to the
 * inverse of how alarming the number looks. A bounce is the least useful
 * outcome there is: the address is dead and there is nobody left to sell to.
 * A contact who clicked a link and never wrote back is the most useful one,
 * because they are reachable, interested, and nobody has followed up.
 *
 * Every count comes from the funnel the Contacts tab has already loaded, so
 * this costs no request. `tile` is the key of the nav tile that filters the
 * table to that group -- the page turns it into a working button.
 */
export const contactOutcomeFindings = (funnel = {}) => {
  const n = key => Number(funnel[key]) || 0;
  const added = n("contactsAdded");
  const completed = n("completedCampaigns");
  const opened = n("contactsOpened");
  const clicked = n("contactsClicked");
  const replied = n("totalReplyCount");
  const meetings = n("meetingBookedCount");
  const autoReplies = n("autoReplyCount");
  const failed = n("emailsFailed");
  const bounced = n("emailsBounced");
  const ongoing = n("ongoing");
  const optedOut = n("optedOut");
  const out = [];

  // Clicked but silent — highest intent in the sequence, and nothing in the
  // product surfaces it today.
  if (clicked > 0 && clicked > replied) {
    out.push({
      tone: "hot",
      label: "Interested, never replied",
      text: `${clicked} contact${clicked === 1 ? "" : "s"} clicked a link but ${clicked === 1 ? "did" : "did"} not write back. They read it and acted — they are the warmest people in this sequence.`,
      tile: "clickCount"
    });
  }

  // The big one: sequence exhausted, no answer. Not dead, just unconvinced.
  if (completed > 0) {
    out.push({
      tone: "warn",
      label: "Finished the sequence in silence",
      text: `${completed} contact${completed === 1 ? "" : "s"} received every step and never replied. This sequence is done with them — they are not waiting on anything.`,
      tile: "Completed",
      second: { label: "Re-enrol later", key: "reactivate" }
    });
  }

  // Opened, did not click, did not reply: a message problem, not a targeting or
  // deliverability one. Worth separating from "never opened".
  const readOnly = opened - clicked;
  if (opened > 0 && readOnly > 0 && clicked < opened) {
    out.push({
      tone: "neutral",
      label: "Reading, not acting",
      text: `${opened} opened but only ${clicked} clicked. They are seeing it and not biting, which points at the offer rather than the subject line.`,
      tile: "openCount"
    });
  }

  // Auto-replies are not people. They inflate the reply rate, and the typed
  // ones (left the company, address changed) mean the contact record is wrong.
  if (autoReplies > 0) {
    out.push({
      tone: "warn",
      label: "Auto-replies, not people",
      text: `${autoReplies} of the replies ${autoReplies === 1 ? "was an" : "were"} automatic — out-of-office, a full inbox, or someone who has left the company. Worth separating from your ${replied} real repl${replied === 1 ? "y" : "ies"}.`,
      tile: "auto_reply",
      second: { label: "Snooze and re-enrol", key: "snooze" }
    });
  }

  // Failed is not bounced: these are our problem (limits, missing inbox) and
  // the product already offers Retry for them.
  if (failed > 0) {
    out.push({
      tone: "warn",
      label: "Failed on our side",
      text: `${failed} send${failed === 1 ? "" : "s"} failed for reasons like a send limit or a missing inbox, not a bad address. These can be retried.`,
      tile: "failed",
      second: { label: "Retry", key: "retry" }
    });
  }

  if (replied > 0) {
    out.push({
      tone: "good",
      label: "Replied",
      text: meetings > 0
        ? `${replied} replied and ${meetings} became meeting${meetings === 1 ? "" : "s"}. Worth checking none of the rest are still waiting on you.`
        : `${replied} replied and none have become meetings yet.`,
      tile: "replied",
      second: { label: "Complete in sequence", key: "complete" }
    });
  }

  if (ongoing > 0) {
    out.push({
      tone: "neutral",
      label: "Still running",
      text: `${ongoing} contact${ongoing === 1 ? " is" : "s are"} mid-sequence.`,
      tile: "ongoing"
    });
  }

  // Last, deliberately: hygiene, not selling.
  if (bounced > 0 && added > 0) {
    out.push({
      tone: "bad",
      label: "Unreachable",
      text: `${bounced} address${bounced === 1 ? "" : "es"} bounced and will never receive anything. Removing them protects the domain everything else sends from.`,
      tile: "bounced",
      second: { label: "Enrich", key: "enrich" }
    });
  }

  if (optedOut > 0) {
    out.push({
      tone: "neutral",
      label: "Opted out",
      tile: "Opted_Out",
      text: `${optedOut} opted out here. They are blocked from every sequence, not just this one.`
    });
  }

  return out;
};

export const setupFindings = ({ missingTokens = [], steps = [] } = {}) => {
  const out = [];
  if (missingTokens.length) {
    out.push({
      tone: "bad",
      label: "Merge tokens without values",
      text: `{{${missingTokens.join("}}, {{")}}} will send blank. Fill them in settings.`
    });
  }
  const gap = Math.max(0, ...steps.map(s => Number(s.delay) || 0));
  if (gap > 10) {
    out.push({
      tone: "warn",
      label: `${gap}-day gap between steps`,
      text: "Prospects forget you after ten days. Tighten the delay."
    });
  }
  return out;
};

// Copy and readiness: what is written into the steps, and whether the
// sequence is otherwise clear to send.
const SPAM_WORDS = /\b(free|guarantee[d]?|act now|limited time|winner|no obligation|risk[- ]free|click here|urgent)\b/gi;
const ALL_CAPS = /^[A-Z\s!]{8,}$/;
const SUBJECT_MAX = 60;
// steps is keyed by step number; each variant carries the channel type, keyed
// "1", "2" -- mirrors readSteps() above and Steps.js's getStageType().
const emailSteps = steps =>
  Object.keys(steps || {})
    .map(key => {
      const step = steps[key] || {};
      const variants = step.variants || {};
      const variantKeys = Object.keys(variants);
      const type = variantKeys.length ? variants[variantKeys[0]]?.type || "" : "";
      return { no: Number(key), variants, variantKeys, type };
    })
    .filter(s => EMAIL_TYPES.includes(s.type))
    .sort((a, b) => a.no - b.no);
const variants = s => s.variantKeys.map(vk => s.variants[vk]);
const firstSubject = s => variants(s)[0]?.subject ?? "";
const firstContent = s => variants(s)[0]?.content ?? "";

export const copyFindings = ({ steps = {}, status = "", blocker = null, contactsAdded = 0, volumeStepNo = null } = {}) => {
  const out = [];
  const emails = emailSteps(steps);
  const opener = emails[0];
  if (opener) {
    const subject = firstSubject(opener);
    if (subject.length > SUBJECT_MAX) {
      out.push({ tone: "warn", key: "subject_long", step: opener.no, label: "Subject too long", text: `${subject.length} characters. Mobile cuts off around ${SUBJECT_MAX}. Keep it to six words.` });
    }
    if (subject && ((subject.match(SPAM_WORDS) || []).length >= 2 || ALL_CAPS.test(subject))) {
      out.push({ tone: "warn", key: "subject_spammy", step: opener.no, label: "Subject may land in promotions", text: "Words like free or guaranteed push email to the promotions tab. Say what it is about." });
    }
    if (!/\{\{/.test(firstContent(opener))) {
      out.push({ tone: "warn", key: "no_personalisation", step: opener.no, label: "Opener has no personalisation", text: "No merge tokens in the first email. Even {{firstName}} and {{company}} lift replies." });
    }
  }
  const subjects = emails.map(firstSubject).filter(Boolean);
  if (subjects.length >= 2 && new Set(subjects.map(s => s.trim().toLowerCase())).size === 1) {
    out.push({ tone: "neutral", key: "same_subject", label: "Same subject on every step", text: "Identical subjects read as a thread nobody answered. Vary step two onward." });
  }
  const volume = emails.find(s => s.no === volumeStepNo);
  if (volume && volume.variantKeys.length < 2) {
    out.push({ tone: "neutral", key: "single_variant", step: volume.no, label: `Step ${volume.no} has one variant`, text: "This step carries your volume. A second variant tells you what works." });
  }
  if (String(status).toUpperCase() === "DRAFT" && !blocker && emails.length && contactsAdded > 0) {
    out.unshift({ tone: "hot", key: "ready_to_activate", label: "Ready to activate", text: `${contactsAdded} contacts, steps and inbox are set. Nothing is stopping the first send but the switch.` });
  }
  return out;
};
